<script setup>
import FormStepper from '@/components/forms/FormStepper.vue'
import { useValidationFieldErrors } from '@/composables/useValidationFieldErrors'
import { useAnaesthetistStore } from '@/stores/anaesthetist'
import { useValidationStore } from '@/stores/validation'
import { $api } from '@/utils/api'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

// Import step components
import HospitalsList from '@/views/pages/anaesthetist/HospitalsList.vue'
import SelectUserPlan from '@/views/pages/anaesthetist/SelectUserPlan.vue'
import SpecialistAreasList from '@/views/pages/anaesthetist/SpecialistAreasList.vue'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const anaesthetistStore = useAnaesthetistStore()
const validationStore = useValidationStore()
const router = useRouter()

const currentStep = ref(0)
const loading = ref(false)
const registrationComplete = ref(false)
const accountCreated = ref(false)
const userEmail = ref('')

// Registration form data
const registrationData = ref({
  first_name: '',
  last_name: '',
  gmc_number: '',
  email: '',
  subscription_tier: 'standard',
  role: 'anaesthetist',
})


const skipMessage = computed(() => '')

// Steps configuration - Soft Registration
const steps = [
  {
    title: 'Account',
    subtitle: 'Create your account',
    icon: 'ri-shield-user-line',
    required: true,
  },
  {
    title: 'Hospitals',
    subtitle: 'Work Locations',
    icon: 'ri-hospital-line',
    required: false,
  },
  {
    title: 'Specialties',
    subtitle: 'Areas of Expertise',
    icon: 'ri-stethoscope-line',
    required: false,
  },
  {
    title: 'Subscription',
    subtitle: 'Choose Plan',
    icon: 'ri-vip-crown-line',
    required: true,
  },
]

// Initialize anaesthetist in create mode
anaesthetistStore.startNewAnaesthetist()

const { fieldError, fieldErrorMessage, clearErrorsFor } = useValidationFieldErrors(validationStore)

const canProceedFromAccount = computed(() => {
  return registrationData.value.first_name
    && registrationData.value.last_name
    && registrationData.value.gmc_number
    && registrationData.value.email
})

// Navigation
const goToNextStep = () => {
  if (currentStep.value === 0 && !canProceedFromAccount.value) {
    validationStore.setErrors({
      message: 'Please complete your personal details and email address.',
      errors: {},
    })
    
    return
  }

  validationStore.clearErrors()
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
  }
}

const goToPreviousStep = () => {
  validationStore.clearErrors()
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// Submit account creation
const submitAccountCreation = async () => {
  loading.value = true
  validationStore.clearErrors()

  try {
    const payload = {
      email: registrationData.value.email,
      subscription_tier: registrationData.value.subscription_tier,
      role: 'anaesthetist',
      first_name: registrationData.value.first_name,
      last_name: registrationData.value.last_name,
      gmc_number: registrationData.value.gmc_number,
    }

    await $api('/api/auth/register', {
      method: 'POST',
      body: payload,
    })

    accountCreated.value = true
    userEmail.value = registrationData.value.email
  }
  catch (err) {
    const payload = err?.response?._data ?? err?.data ?? {}

    validationStore.setErrors({
      message: payload?.message ?? 'Registration failed. Please check your information.',
      errors: payload?.errors || {},
    })
  }
  finally {
    loading.value = false
  }
}

// Submit profile data (hospitals and specialties)
const submitProfileData = async () => {
  loading.value = true
  validationStore.clearErrors()

  try {
    registrationComplete.value = true
    await router.push('/users/register-thank-you')
  }
  catch (err) {
    const payload = err?.response?._data ?? err?.data ?? {}

    validationStore.setErrors({
      message: payload?.message ?? 'Failed to update profile.',
      errors: payload?.errors || {},
    })
  }
  finally {
    loading.value = false
  }
}

const isCurrentStepOptional = computed(() => currentStep.value > 0 && !steps[currentStep.value]?.required)

const skipToComplete = () => {
  currentStep.value = steps.length - 1
}

const handleBack = () => {
  if (currentStep.value === 0) {
    router.push({ name: 'login' })
    
    return
  }

  goToPreviousStep()
}

const submitRegistration = async () => {
  if (currentStep.value === 0) {
    await submitAccountCreation()
    if (accountCreated.value)
      currentStep.value++
  }
  else if (currentStep.value < steps.length - 1) {
    currentStep.value++
  }
  else {
    await submitProfileData()
  }
}

// Watch for step changes to clear errors
watch(currentStep, () => {
  validationStore.clearErrors()
})
</script>

<template>
  <div class="auth-wrapper d-flex ">
    <!-- Left side - Illustration (hidden on mobile) -->
    <VCol
      md="4"
      class="d-none d-md-flex flex-column align-center justify-start pa-10"
      style="background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-primary-darken-1)) 100%);"
    >
      <div class="text-center text-white">
        <VIcon
          icon="ri-stethoscope-line"
          size="120"
          class="mb-8"
        />
        <h2 class="text-h3 font-weight-bold mb-4">
          Join Our Network
        </h2>
        <p class="text-h6 mb-8">
          Connect with hospitals and opportunities across the UK
        </p>
        <div class="d-flex flex-column gap-4 text-left">
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Access to exclusive job listings</span>
          </div>
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Manage your availability easily</span>
          </div>
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Connect with top hospitals</span>
          </div>
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Professional profile showcase</span>
          </div>
        </div>
      </div>
    </VCol>

    <!-- Right side - Registration form -->
    <VCol
      cols="12"
      md="8"
      class="d-flex flex-column align-center justify-start"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        class="registration-card"
        :elevation="0"
      >
        <!-- Logo -->
        <div class="d-flex align-center justify-center mb-8">
          <RouterLink to="/">
            <div class="d-flex align-center gap-3">
              <VNodeRenderer :nodes="themeConfig.app.logo" />
              <h1 class="text-h4">
                {{ themeConfig.app.title }}
              </h1>
            </div>
          </RouterLink>
        </div>

        <!-- Success message -->
        <VExpandTransition>
          <div v-if="registrationComplete">
            <VCard
              class="pa-8"
              elevation="0"
            >
              <div class="text-body-1 text-center">
                <p class="mb-4 text-h5 font-weight-bold">
                  Thank you for your registration
                </p>
                <p class="mb-4 text-medium-emphasis">
                  Your request has been received successfully.
                </p>
                <p class="mb-6 text-medium-emphasis">
                  Our staff will review and activate your account within 48 hours.
                </p>
              </div>
              <VBtn
                color="primary"
                size="large"
                href="/"
                prepend-icon="ri-home-4-line"
              >
                Back to institutional site
              </VBtn>
            </VCard>
          </div>
        </VExpandTransition>

        <!-- Error message -->
        <VExpandTransition>
          <VAlert
            v-if="Object.keys(validationStore.errors).length"
            type="error"
            variant="tonal"
            class="mb-6"
            closable
            @click:close="validationStore.clearErrors()"
          >
            {{ validationStore.message || 'Please correct the errors below' }}
          </VAlert>
        </VExpandTransition>

        <div v-if="!registrationComplete">
          <FormStepper
            :current-step="currentStep"
            :steps="steps"
            :loading="loading"
            :is-optional="isCurrentStepOptional"
            :skip-message="skipMessage"
            :fixed-navigation="true"
            back-label="Back"
            next-label="Next"
            complete-label="Complete Registration"
            skip-label="Skip"
            @update:current-step="currentStep = $event"
            @back="handleBack"
            @next="submitRegistration"
            @skip="skipToComplete"
          >
            <!-- Steps Content -->
            <VWindow
              v-model="currentStep"
              class="disable-tab-transition"
            >
              <!-- Step 0: Account -->
              <VWindowItem :value="0">
                <VCard>
                  <VCardText>
                    <div class="text-h6 mb-6">
                      Account Information
                    </div>
                    <VRow>
                      <VCol
                        cols="12"
                        md="6"
                      >
                        <VTextField
                          v-model="registrationData.first_name"
                          label="First name"
                          placeholder="Enter your first name"
                          variant="outlined"
                          prepend-inner-icon="ri-user-line"
                          :error="fieldError('first_name')"
                          :error-messages="fieldErrorMessage('first_name')"
                          required
                          @focus="clearErrorsFor('first_name')"
                        />
                      </VCol>

                      <VCol
                        cols="12"
                        md="6"
                      >
                        <VTextField
                          v-model="registrationData.last_name"
                          label="Last name"
                          placeholder="Enter your last name"
                          variant="outlined"
                          prepend-inner-icon="ri-user-line"
                          :error="fieldError('last_name')"
                          :error-messages="fieldErrorMessage('last_name')"
                          required
                          @focus="clearErrorsFor('last_name')"
                        />
                      </VCol>

                      <VCol cols="12">
                        <VTextField
                          v-model="registrationData.gmc_number"
                          label="GMC Number"
                          placeholder="Enter your GMC number"
                          variant="outlined"
                          prepend-inner-icon="ri-id-card-line"
                          :error="fieldError('gmc_number')"
                          :error-messages="fieldErrorMessage('gmc_number')"
                          required
                          @focus="clearErrorsFor('gmc_number')"
                        />
                      </VCol>

                      <VCol cols="12">
                        <VTextField
                          v-model="registrationData.email"
                          label="Email address"
                          placeholder="your.email@example.com"
                          type="email"
                          variant="outlined"
                          prepend-inner-icon="ri-mail-line"
                          :error="fieldError('email')"
                          :error-messages="fieldErrorMessage('email')"
                          required
                          @focus="clearErrorsFor('email')"
                        />
                      </VCol>
                      <VCol cols="12">
                        <VAlert
                          type="info"
                          variant="tonal"
                        >
                          We will email you a secure link to confirm your email address and choose a password.
                        </VAlert>
                      </VCol>
                    </VRow>
                  </VCardText>
                </VCard>
              </VWindowItem>

              <!-- Step 1: Hospitals -->
              <VWindowItem :value="1">
                <div class="d-flex flex-column gap-6">
                  <VAlert
                    type="info"
                    variant="tonal"
                    class="optional-step-alert"
                  >
                    <div class="optional-step-alert-content">
                      <span class="text-body-2 text-medium-emphasis">
                        This step is optional. By selecting hospitals, you'll see job listings specifically related to your selected hospitals. You can add more later.
                      </span>
                      <VBtn
                        variant="outlined"
                        color="secondary"
                        :disabled="loading"
                        @click="skipToComplete"
                      >
                        <VIcon
                          icon="ri-skip-right-line"
                          end
                        />
                        Skip
                      </VBtn>
                    </div>
                  </VAlert>

                  <VCard flat>
                    <VCardText>
                      <h4 class="text-h4 mb-1">
                        Associated Hospitals
                      </h4>
                      <p class="text-body-1 mb-6">
                        Select the hospitals where you work
                      </p>
                      <HospitalsList />
                    </VCardText>
                  </VCard>
                </div>
              </VWindowItem>

              <!-- Step 2: Specialist Areas -->
              <VWindowItem :value="2">
                <div class="d-flex flex-column gap-6">
                  <VAlert
                    type="info"
                    variant="tonal"
                    class="optional-step-alert"
                  >
                    <div class="optional-step-alert-content">
                      <span class="text-body-2 text-medium-emphasis">
                        This step is optional. By selecting hospitals, you'll see job listings specifically related to your selected hospitals. You can add more later.
                      </span>
                      <VBtn
                        variant="outlined"
                        color="secondary"
                        :disabled="loading"
                        @click="skipToComplete"
                      >
                        <VIcon
                          icon="ri-skip-right-line"
                          end
                        />
                        Skip
                      </VBtn>
                    </div>
                  </VAlert>

                  <VCard flat>
                    <VCardText>
                      <h4 class="text-h4 mb-1">
                        Specialist Areas
                      </h4>
                      <p class="text-body-1 mb-6">
                        Select your areas of expertise
                      </p>
                      <SpecialistAreasList />
                    </VCardText>
                  </VCard>
                </div>
              </VWindowItem>

              <!-- Step 3: Subscription -->
              <VWindowItem :value="3">
                <SelectUserPlan
                  v-model="registrationData.subscription_tier"
                  :is-registration="true"
                />
              </VWindowItem>
            </VWindow>
          </FormStepper>
        </div>
      </VCard>
    </VCol>
  </div>
</template>

<style lang="scss" scoped>
.auth-wrapper {
  min-height: 100vh;
}

.registration-card {
  inline-size: 100%;
  max-inline-size: 100%;
  padding-block-end: 110px;
}


.disable-tab-transition {
  overflow: visible !important;
}

// Navigation styles are in resources/styles/navigation.scss

@media (max-width: 959px) {
  .registration-card {
    padding-block-end: 170px;
  }
}

.optional-step-alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
</style>
